import { Box } from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import AdvancedTable from "../../components/advancedTable/AdvancedTable";
import { setDataDateRange, setNewFilter } from "../../components/slices/advancedTable";
import { filterDataBasedOnAttributes } from "../../app/utils/middleware";
import { useFetchTasksDataForSystemTasks } from "../../hooks/advancedTableHook";
import { mapFilterValueToId, TASK_FIELDS } from "../../app/constants/config/systemTasks";
import { TabHeader } from "./Tabs";
import { mainTabItems, openedSubTabItems, slicesConfigReferenceMap } from "./config/tabOptions";
import { selectActiveTaskTab, setActiveTaskTab } from "./slice/taskSlice";
import { TAB_OPTIONS } from "../../app/constants/config/systemTasks";

const SystemsTable = ({ sliceItemConfig }) => {
  const systemTaskConfig = sliceItemConfig.getConfig();
  return (
    <AdvancedTable
      config={systemTaskConfig}
      apiHookWithParams={[
        useFetchTasksDataForSystemTasks,
        [sliceItemConfig.taskStatusArray, systemTaskConfig.tableProps.columnProps, sliceItemConfig.isBackedUp],
      ]}
    />
  );
};

export default function TasksLayout() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useSelector(selectActiveTaskTab);
  
  // Read filter parameters from query string
  const priorityParam = searchParams.get("priority");
  const typeParam = searchParams.get("type");
  const statusParam = searchParams.get("status");
  const dateRangeParam = searchParams.get("dateRange");
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");

  // Check if we have any filters from dashboard redirect
  const hasFilterParams = !!(priorityParam || typeParam || statusParam || dateRangeParam);

  const isOpenedSubTab = openedSubTabItems.some((item) => item.id === activeTab);
  const isOpenedMainTab = activeTab === TAB_OPTIONS.OPENED_TASKS.ID;
  
  // Determine the actual active tab to use for data loading
  // If main tab "opened-tasks" is selected, use the first sub-tab instead
  const actualActiveTab = isOpenedMainTab ? openedSubTabItems[0]?.id : activeTab;
  const activeMainTab = isOpenedSubTab || isOpenedMainTab ? TAB_OPTIONS.OPENED_TASKS.ID : activeTab;
  const activeSubTab = isOpenedSubTab ? activeTab : openedSubTabItems[0]?.id;
  
  // Navigate to Completed Tasks tab if coming from dashboard with filters
  useEffect(() => {
    if (hasFilterParams && activeTab !== TAB_OPTIONS.COMPLETED_TASKS.ID) {
      dispatch(setActiveTaskTab(TAB_OPTIONS.COMPLETED_TASKS.ID));
    }
  }, [hasFilterParams, activeTab, dispatch]);
  
  // Apply filters when query params change. Wait until the Completed Tasks tab
  // switch above has actually taken effect — dispatching immediately on the same
  // pass as the tab switch would apply the filter to the table that was active
  // *before* the redirect (setActiveTaskTab only takes effect next render), so
  // the filter would silently land on the wrong AdvancedTable entity.
  useEffect(() => {
    if (!hasFilterParams) return;
    if (activeTab !== TAB_OPTIONS.COMPLETED_TASKS.ID) return;

    if (priorityParam) {
      const mappedFilter = mapFilterValueToId(TASK_FIELDS.PRIORITY.ID, priorityParam);
      if (mappedFilter) {
        dispatch(
          setNewFilter({
            columnId: TASK_FIELDS.PRIORITY.ID,
            columnLabel: "Priority",
            columnValues: [mappedFilter],
            resetFilter: true,
          })
        );
      }
    }
    if (typeParam) {
      const mappedFilter = mapFilterValueToId(TASK_FIELDS.TYPE.ID, typeParam);
      if (mappedFilter) {
        dispatch(
          setNewFilter({
            columnId: TASK_FIELDS.TYPE.ID,
            columnLabel: "Type",
            columnValues: [mappedFilter],
            resetFilter: true,
          })
        );
      }
    }
    if (statusParam) {
      const mappedFilters = statusParam
        .split(",")
        .map((value) => mapFilterValueToId(TASK_FIELDS.STATUS.ID, value.trim()))
        .filter(Boolean);
      if (mappedFilters.length) {
        dispatch(
          setNewFilter({
            columnId: TASK_FIELDS.STATUS.ID,
            columnLabel: "Status",
            columnValues: mappedFilters,
            resetFilter: true
          })
        );
      }
    }
    if (dateRangeParam) {
      const parsedValue = Number(dateRangeParam);
      dispatch(
        setDataDateRange({
          value: Number.isNaN(parsedValue) ? dateRangeParam : parsedValue,
          from: fromParam || null,
          to: toParam || null,
        })
      );
    }

    // setNewFilter only records the filter on the table entity — the visible
    // rows aren't recomputed until filterDataBasedOnAttributes runs (normally
    // triggered by FilterDataDialogModal's own effect, which never mounts on
    // this redirect path). Force that recompute now so the filter is actually
    // visible immediately, not just whenever the next fetch happens to occur.
    if (priorityParam || typeParam || statusParam) {
      const columnProps = slicesConfigReferenceMap[TAB_OPTIONS.COMPLETED_TASKS.ID].getConfig().tableProps.columnProps;
      dispatch(filterDataBasedOnAttributes({ payload: { filterTable: true }, columnProps }));
    }

    setSearchParams({});

  }, [
    hasFilterParams,
    activeTab,
    priorityParam,
    typeParam,
    statusParam,
    dateRangeParam,
    fromParam,
    toParam,
    dispatch,
    setSearchParams,
  ]);

  return (
    activeTab && (
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Box sx={{ position: "sticky", top: 0, zIndex: 100 }}>
          <TabHeader tabItems={mainTabItems} activeTabOverride={activeMainTab} />
          {!!(isOpenedSubTab || isOpenedMainTab) && (
            <TabHeader
              tabItems={openedSubTabItems}
              activeTabOverride={activeSubTab}
              includeBorderTop={false}
            />
          )}
        </Box>
        <SystemsTable sliceItemConfig={slicesConfigReferenceMap[actualActiveTab]} />
      </Box>
    )
  );
}
